import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Z_INDEX } from '../../shared/constants/theme';

const ACCENT = '#4ce1b6';

export const RATE_SUCCESS_COPY = {
  groupChannel: {
    title: 'Rates submitted',
    message:
      'Your rates have been sent to all selected OTAs. They typically appear on booking sites within a few minutes. You can continue working while the channels update.',
  },
  groupPms: {
    title: 'Rates saved',
    message:
      'Your PMS rates have been saved for the selected dates and are now available for in-house bookings.',
  },
  updateRates: {
    title: 'Rates saved',
    message:
      'Your rates have been saved for the selected dates and are now available for in-house bookings.',
  },
  stopSellApplyChannel: {
    title: 'Stop sell submitted',
    message:
      'Stop sell has been sent to the selected OTAs. These rate plans will close for new bookings within a few minutes.',
  },
  stopSellClearChannel: {
    title: 'Stop sell cleared',
    message:
      'Stop sell has been cleared on the selected OTAs. These rate plans will reopen for bookings within a few minutes.',
  },
  stopSellApplyPms: {
    title: 'Stop sell applied',
    message:
      'Stop sell has been applied in your PMS. These rate plans are now closed for the selected dates.',
  },
  stopSellClearPms: {
    title: 'Stop sell cleared',
    message:
      'Stop sell has been cleared in your PMS. These rate plans are open again for the selected dates.',
  },
  syncRates: {
    title: 'Sync submitted',
    message:
      'Your current rates have been sent to all OTAs. Updates typically appear on booking sites within a few minutes.',
  },
  restrictionsApply: {
    title: 'Restrictions submitted',
    message:
      'Rate restrictions have been sent to the selected OTAs. They typically appear on booking sites within a few minutes.',
  },
  restrictionsClear: {
    title: 'Restrictions cleared',
    message:
      'Rate restrictions have been cleared on the selected OTAs. Updates typically appear within a few minutes.',
  },
  inventoryAvailable: {
    title: 'Available inventory synced',
    message:
      'Current remaining rooms for each date have been synced to all selected channels. Booking sites typically update within a few minutes.',
  },
  inventoryRestrictionsApply: {
    title: 'Restrictions submitted',
    message:
      'Inventory restrictions have been sent to the selected OTAs. They typically appear on booking sites within a few minutes.',
  },
  inventoryRestrictionsClear: {
    title: 'Restrictions cleared',
    message:
      'Inventory restrictions have been cleared on the selected OTAs. Updates typically appear within a few minutes.',
  },
  inventoryStopSellApply: {
    title: 'Stop sell submitted',
    message:
      'Stop sell has been sent to the selected OTAs. These rooms will close for new bookings within a few minutes.',
  },
  inventoryStopSellClear: {
    title: 'Stop sell cleared',
    message:
      'Stop sell has been cleared on the selected OTAs. These rooms will reopen for bookings within a few minutes.',
  },
  inventoryBlockCreate: {
    title: 'Event block created',
    message:
      'The event block has been created. Inventory for the selected rooms is now reserved for those dates.',
  },
  inventoryBlockUpdate: {
    title: 'Event block updated',
    message:
      'The event block has been updated. Inventory for the selected rooms now reflects the new dates and rooms.',
  },
};

export default function RateActionSuccessDialog({ open, title, message, onClose, container }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const scoped = Boolean(container);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      container={container}
      sx={{
        position: scoped ? 'absolute' : 'fixed',
        zIndex: Z_INDEX.MODAL,
      }}
      BackdropProps={{
        sx: {
          position: scoped ? 'absolute' : 'fixed',
          backgroundColor: alpha('#04120e', isDark ? 0.62 : 0.38),
          backdropFilter: 'blur(10px)',
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          position: 'relative',
          overflow: 'hidden',
          mx: scoped ? 2 : 2.5,
          maxWidth: scoped ? 'calc(100% - 32px)' : 360,
          borderRadius: 3,
          border: `1px solid ${alpha(ACCENT, isDark ? 0.4 : 0.28)}`,
          background: isDark
            ? `linear-gradient(180deg, ${alpha(ACCENT, 0.16)} 0%, #0f172a 38%, #0b1220 100%)`
            : `linear-gradient(180deg, ${alpha(ACCENT, 0.22)} 0%, #ffffff 42%, #f8fffc 100%)`,
          boxShadow: isDark
            ? `0 28px 64px ${alpha('#000', 0.55)}, 0 0 0 1px ${alpha(ACCENT, 0.12)}`
            : `0 24px 56px ${alpha('#0f172a', 0.18)}, 0 0 0 1px ${alpha(ACCENT, 0.1)}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            height: 4,
            background: `linear-gradient(90deg, ${alpha(ACCENT, 0.2)} 0%, ${ACCENT} 50%, ${alpha(ACCENT, 0.2)} 100%)`,
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        aria-label="Close"
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          zIndex: 1,
          color: isDark ? alpha('#fff', 0.62) : '#64748b',
          bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#0f172a', 0.04),
          '&:hover': {
            bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08),
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ px: 2.5, pt: 3, pb: 0.75, textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 1.5,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            background: `radial-gradient(circle at 35% 30%, ${alpha('#fff', isDark ? 0.18 : 0.7)} 0%, ${alpha(ACCENT, isDark ? 0.22 : 0.28)} 100%)`,
            border: `2px solid ${alpha(ACCENT, isDark ? 0.55 : 0.65)}`,
            boxShadow: `0 12px 28px ${alpha(ACCENT, 0.35)}, inset 0 1px 0 ${alpha('#fff', 0.45)}`,
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: ACCENT,
              color: '#04120e',
              boxShadow: `0 6px 14px ${alpha(ACCENT, 0.4)}`,
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 26, fontWeight: 800 }} />
          </Box>
        </Box>

        <Typography
          variant="overline"
          sx={{
            display: 'block',
            color: isDark ? ACCENT : '#0f766e',
            fontWeight: 800,
            letterSpacing: 1.4,
            mb: 0.25,
            fontSize: '0.7rem',
          }}
        >
          Success
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            letterSpacing: -0.2,
            color: isDark ? '#f8fafc' : '#0f172a',
            mb: 0.75,
          }}
        >
          {title || 'Done'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: isDark ? alpha('#e2e8f0', 0.78) : '#475569',
            lineHeight: 1.55,
            fontWeight: 500,
            maxWidth: 300,
            mx: 'auto',
          }}
        >
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{
            minHeight: 40,
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 800,
            fontSize: '0.875rem',
            backgroundColor: ACCENT,
            color: '#04120e',
            boxShadow: `0 8px 16px ${alpha(ACCENT, 0.32)}`,
            '&:hover': {
              backgroundColor: ACCENT,
              opacity: 0.92,
              boxShadow: `0 10px 20px ${alpha(ACCENT, 0.4)}`,
            },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
